import React from 'react';
import {
  AgnesBadge,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesModal,
  AgnesPageHeader,
  AgnesTooltip,
} from '@agnes/ui';
import { Building2, Eye, FileText } from 'lucide-react';
import { useEmpresasStore } from '@/stores/useEmpresasStore';
import { useColetaConfigStore } from '@/stores/useColetaConfigStore';
import ColetaAutomaticaModal from '@/components/empresas/ColetaAutomaticaModal';
import { authStorage } from '@/lib/authStorage';
import UtilsMask from '@/utils/UtilsMask';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Empresa = any;

// detalhe read-only da empresa (dados + estabelecimentos do grupo)
function DetalheEmpresa() {
  const detalhe = useEmpresasStore((s) => s.detalhe);
  const carregando = useEmpresasStore((s) => s.carregandoDetalhe);
  const fechar = useEmpresasStore((s) => s.fecharDetalhe);
  const open = detalhe !== null || carregando;

  const campos: [string, React.ReactNode][] = detalhe
    ? [
        ['Razão social', detalhe.socialName ?? '-'],
        ['Nome fantasia', detalhe.tradeName ?? '-'],
        ['CNPJ', UtilsMask.cnpj(detalhe.cnpj)],
        ['Nº inscrição', detalhe.nrinsc ?? '-'],
        ['UF', detalhe.stateCode ?? '-'],
        ['Cidade', detalhe.city ?? '-'],
        ['Tipo', detalhe.matriz ? 'Matriz' : 'Filial'],
        ['Situação', detalhe.isActive ? 'Ativa' : 'Inativa'],
      ]
    : [];

  const estabColumns: AgnesDataTableColumn<Empresa>[] = [
    { key: 'socialName', header: 'Razão social', width: 260, render: (r) => r.socialName ?? '-' },
    { key: 'cnpj', header: 'CNPJ', width: 180, render: (r) => UtilsMask.cnpj(r.cnpj) },
    { key: 'stateCode', header: 'UF', width: 70, render: (r) => r.stateCode ?? '-' },
    { key: 'city', header: 'Cidade', width: 180, render: (r) => r.city ?? '-' },
    { key: 'tipo', header: 'Tipo', width: 90, render: (r) => (r.matriz ? 'Matriz' : 'Filial') },
  ];

  return (
    <AgnesModal
      open={open}
      title="Detalhes da empresa"
      maxWidth="lg"
      onClose={fechar}
      footer={
        <div className="flex justify-end">
          <AgnesButton variant="text" onClick={fechar}>
            Fechar
          </AgnesButton>
        </div>
      }
    >
      {carregando || !detalhe ? (
        <div className="py-8 text-center text-agnes-text-secondary">Carregando…</div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {campos.map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <span className="text-xs text-agnes-text-secondary">{label}</span>
                <span className="text-sm">{value}</span>
              </div>
            ))}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">
              Estabelecimentos ({detalhe.estabelecimentos?.length ?? 0})
            </p>
            {detalhe.estabelecimentos?.length ? (
              <div className="overflow-x-auto rounded-xl border border-agnes-border">
                <AgnesDataTable<Empresa>
                  columns={estabColumns}
                  data={detalhe.estabelecimentos}
                  rowKey={(r) => String(r.id ?? r.cnpj)}
                />
              </div>
            ) : (
              <p className="text-sm text-agnes-text-secondary">Nenhum estabelecimento no grupo.</p>
            )}
          </div>
        </div>
      )}
    </AgnesModal>
  );
}

export default function PageEmpresas() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const lista = useEmpresasStore((s) => s.lista);
  const carregando = useEmpresasStore((s) => s.carregando);

  React.useEffect(() => {
    // só busca no BFF se autenticado; sem token (dev local) evita redirect ao login
    if (authStorage.getItem('access_Token')) useEmpresasStore.getState().buscar();
  }, []);

  const data: Empresa[] = lista;

  const trunc = (v: string, w: number) => (
    <span className="block truncate" style={{ maxWidth: w - 20 }} title={v}>
      {v}
    </span>
  );
  const columns: AgnesDataTableColumn<Empresa>[] = [
    {
      key: 'socialName',
      header: 'Razão social',
      width: 300,
      render: (r) => trunc(r.socialName ?? '-', 300),
    },
    { key: 'cnpj', header: 'CNPJ matriz', width: 180, render: (r) => UtilsMask.cnpj(r.cnpj) },
    { key: 'cnae', header: 'CNAE', width: 120, render: (r) => r.cnae ?? '-' },
    { key: 'stateCode', header: 'UF', width: 80, render: (r) => r.stateCode ?? '-' },
    { key: 'city', header: 'Cidade', width: 200, render: (r) => r.city ?? '-' },
    {
      key: 'certificateName',
      header: 'Certificado digital',
      width: 240,
      render: (r) => trunc(r.certificateName ?? '-', 240),
    },
    {
      key: 'outorgaStatus',
      header: 'Outorga',
      width: 130,
      render: (r) =>
        r.outorgaStatus === 'valid' ? (
          <AgnesBadge variant="success">Válida</AgnesBadge>
        ) : r.outorgaStatus === 'invalid' ? (
          <AgnesBadge variant="error">Inválida</AgnesBadge>
        ) : (
          <AgnesBadge variant="secondary">Não verificada</AgnesBadge>
        ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 170,
      align: 'center',
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesTooltip content="Detalhes">
            <AgnesIconButton
              aria-label="Detalhes"
              size="sm"
              onClick={() => useEmpresasStore.getState().abrirDetalhe(r.cnpj)}
            >
              <Eye className="h-4 w-4" />
            </AgnesIconButton>
          </AgnesTooltip>
          <AgnesTooltip content="Coleta automática">
            <AgnesIconButton
              aria-label="Coleta automática"
              size="sm"
              onClick={() => useColetaConfigStore.getState().abrir(r.cnpj, r.certificateName ?? '')}
            >
              <FileText className="h-4 w-4" />
            </AgnesIconButton>
          </AgnesTooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<Building2 />}
        title="Empresas"
        subtitle="Gerencie os certificados e a periodicidade de coleta de certidões das empresas"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Configurações' },
          { label: 'Empresas' },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <TableWrapper>
            <AgnesDataTable<Empresa>
              columns={columns}
              data={data}
              rowKey={(r) => String(r.id ?? r.cnpj)}
              loading={carregando}
              pagination={{
                page,
                pageSize,
                total: data.length,
                onPageChange: setPage,
                onPageSizeChange: (s) => {
                  setPageSize(s);
                  setPage(0);
                },
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>

      <DetalheEmpresa />
      <ColetaAutomaticaModal />
    </>
  );
}
